import { useState } from 'react'
import { Badge, Button, Group, Paper, Stack, Text, TextInput, UnstyledButton } from '@mantine/core'
import type { QuestionItem } from '@/types'

type QuestionMessageProps = {
  toolCallId: string
  questions: QuestionItem[]
  onResume: (body: Record<string, unknown>) => Promise<void>
}

export default function QuestionMessage({ toolCallId, questions, onResume }: QuestionMessageProps) {
  const [selections, setSelections] = useState<string[][]>(() => questions.map(() => []))
  const [customInputs, setCustomInputs] = useState<string[]>(() => questions.map(() => ''))
  const [customActive, setCustomActive] = useState<boolean[]>(() => questions.map(() => false))
  const [submitting, setSubmitting] = useState(false)

  const toggleOption = (qIndex: number, label: string) => {
    const question = questions[qIndex]
    setSelections(prev => {
      const updated = [...prev]
      const current = updated[qIndex]
      if (question.multiple) {
        updated[qIndex] = current.includes(label)
          ? current.filter(l => l !== label)
          : [...current, label]
      } else {
        updated[qIndex] = current.includes(label) ? [] : [label]
        // Deactivate custom input when selecting an option in single-select
        setCustomActive(prev => {
          const ca = [...prev]
          ca[qIndex] = false
          return ca
        })
      }
      return updated
    })
  }

  const toggleCustom = (qIndex: number) => {
    const question = questions[qIndex]
    setCustomActive(prev => {
      const updated = [...prev]
      updated[qIndex] = !updated[qIndex]
      if (!question.multiple && !prev[qIndex]) {
        // Single-select: clear other selections when activating custom
        setSelections(s => {
          const u = [...s]
          u[qIndex] = []
          return u
        })
      }
      return updated
    })
  }

  const updateCustomInput = (qIndex: number, value: string) => {
    setCustomInputs(prev => {
      const updated = [...prev]
      updated[qIndex] = value
      return updated
    })
  }

  const buildAnswers = (): string[][] => {
    return questions.map((_, qIndex) => {
      const selected = [...selections[qIndex]]
      if (customActive[qIndex] && customInputs[qIndex].trim()) {
        selected.push(customInputs[qIndex].trim())
      }
      return selected
    })
  }

  const handleSubmit = async () => {
    if (submitting) return

    setSubmitting(true)
    try {
      const answers = buildAnswers()
      await onResume({ tool_call_id: toolCallId, answers })
    } catch {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (submitting) return

    setSubmitting(true)
    try {
      await onResume({ tool_call_id: toolCallId, rejected: true })
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <Paper p="lg" radius="lg" style={{ maxWidth: 760 }} withBorder>
        <Stack gap="lg">
        {questions.map((q, qIndex) => (
          <Stack key={qIndex} gap="sm">
            <Group gap="xs">
              <Badge color="blue" variant="light">
                {q.header}
              </Badge>
            </Group>
            <Text fw={600} size="sm">{q.question}</Text>
            <Stack gap="xs">
              {q.options.map((opt) => {
                const isSelected = selections[qIndex].includes(opt.label)
                return (
                  <UnstyledButton
                    key={opt.label}
                    type="button"
                    onClick={() => toggleOption(qIndex, opt.label)}
                    disabled={submitting}
                    style={(theme) => ({
                      width: '100%',
                      borderRadius: theme.radius.md,
                      border: `1px solid ${isSelected ? theme.colors.blue[4] : theme.colors.gray[3]}`,
                      backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
                      padding: theme.spacing.sm,
                    })}
                  >
                    <Text fw={500} size="sm">{opt.label}</Text>
                    <Text c="dimmed" size="xs" mt={4}>{opt.description}</Text>
                  </UnstyledButton>
                )
              })}

              <UnstyledButton
                type="button"
                onClick={() => toggleCustom(qIndex)}
                disabled={submitting}
                style={(theme) => ({
                  width: '100%',
                  borderRadius: theme.radius.md,
                  border: `1px solid ${customActive[qIndex] ? theme.colors.blue[4] : theme.colors.gray[3]}`,
                  backgroundColor: customActive[qIndex] ? theme.colors.blue[0] : theme.white,
                  padding: theme.spacing.sm,
                })}
              >
                <Text fw={500} size="sm">Type your own answer</Text>
              </UnstyledButton>
              {customActive[qIndex] && (
                <TextInput
                  value={customInputs[qIndex]}
                  onChange={e => updateCustomInput(qIndex, e.target.value)}
                  placeholder="Type your answer..."
                  disabled={submitting}
                  autoFocus
                />
              )}
            </Stack>
          </Stack>
        ))}

        <Group gap="sm">
          <Button size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
          <Button size="sm" variant="default" onClick={handleReject} disabled={submitting}>
            Dismiss
          </Button>
        </Group>
        </Stack>
      </Paper>
    </div>
  )
}
